import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePostHog } from 'posthog-js/react';
import { useAppDispatch, useAppSelector } from '../useAppStore';
import { getAuthenticateUser, getTokenDetails } from '../../store/authenticate/selectors';
import { useError } from '../useCommon';
import { QUERY_KEYS } from '../../utils/enums';
import {
  IBaseResponse,
  TAddCommissionPlanRes,
  TAxiosError,
  TGetCommissionPlansRes,
  TGetCommissionTeamsRes,
  TGetProductVariantRes,
} from '../../types/responses';
import { TCommissionPlan } from '../../types';
import { CommissionsController } from '../../services/commissions.api';
import {
  TAddCommissionPlanReq,
  TAddCommissionTeamReq,
  TAssignCommissionPlansReq, TUpdateCommissionTeamMembersReq, TUpdateCommissionTeamPlanReq,
} from '../../types/requests';
import { commissionsActions } from '../../store/commissions/slice';
import { getMemberEmails, getSelectedCommissionPlan } from '../../store/commissions/selectors';
import { useGetTeamAPI } from './useTeam';
import { ROUTES } from '../../data';

export function useGetCommissionsPlansAPI() {
  const { personnel } = useAppSelector(getAuthenticateUser);
  const { refetch } = useGetTeamAPI();

  const dispatch = useAppDispatch();

  return useQuery(
    [QUERY_KEYS.COMMISSIONS.GET_PLANS],
    async () => {
      const data = {
        shopName: personnel.shopName,
      };

      const rsp = await CommissionsController().getCommissionsPlans<TGetCommissionPlansRes>(data);

      if (rsp.success && 'commissionPlans' in rsp) {
        refetch();
        dispatch(commissionsActions.setCommissionPlans(rsp.commissionPlans));
      }

      return rsp;
    },
    {
      staleTime: 3 * (60 * 1000),
      suspense: false,
      enabled: true,
    },
  );
}

export function useAssignCommissionPlanAPI() {
  const { personnel } = useAppSelector(getAuthenticateUser);
  const queryClient = useQueryClient();
  const memberEmails = useAppSelector(getMemberEmails);
  const posthog = usePostHog();

  const { handleReqError } = useError();

  const assignCommissionPlanApi = (payload: Omit<TAssignCommissionPlansReq, 'shopName' & 'memberEmails'>) => {
    const data = {
      commissionPlan: payload.commissionPlan,
      memberEmails,
      shopName: personnel.shopName,
    } as TAssignCommissionPlansReq;

    return CommissionsController().assignCommissionPlan<IBaseResponse>(data);
  };

  return useMutation(assignCommissionPlanApi, {
    onSuccess: async (rsp, variables) => {
      if (rsp.success) {
        await queryClient.invalidateQueries({ queryKey: [`${QUERY_KEYS.TEAM}-${personnel?.email}`] });

        posthog.capture('ManageMate_web', {
          shopName: personnel?.shopName,
          newMember: personnel?.email,
          addedAt: new Date().toISOString(),
          action: 'user_assigned_commission_plan',
        });
      } else if (rsp?.error && rsp.error?.response?.status) {
        handleReqError(rsp.error, variables);
      } else if ('msg' in rsp) {
        console.error(`ERROR! assign commission plan failed! ${rsp.msg}`);
      }

      return rsp;
    },
    onError: (error: TAxiosError) => {
      console.error(`ERROR! assign commission plan request threw an Exception! ${error}`);
    },
  });
}

export function useAddCommissionsPlanAPI() {
  const { personnel } = useAppSelector(getAuthenticateUser);
  const { mutateAsync: assignMembers } = useAssignCommissionPlanAPI();
  const { enqueueSnackbar } = useSnackbar();
  const posthog = usePostHog();

  const navigate = useNavigate();

  const { handleReqError } = useError();

  const queryClient = useQueryClient();
  const { t } = useTranslation('commissions');

  const tokenDetails = useAppSelector(getTokenDetails);

  const addCommissionPlanApi = (extraData: Omit<TAddCommissionPlanReq['planData'], 'creator' & 'updatedBy'>) => {
    const data = {
      planData: {
        ...extraData,
        creator: {
          name: personnel.shopName,
          email: personnel.email,
          avatarFile: personnel.avatarFile || '',
          locale: tokenDetails?.settings?.ui?.locale || 'en',
        },
        updatedBy: {
          name: personnel.shopName,
          email: personnel.email,
          avatarFile: personnel.avatarFile || '',
          locale: tokenDetails?.settings?.ui?.locale || 'en',
        },
      },
      shopName: personnel.shopName,
    };

    return CommissionsController().addCommissionPlan<TAddCommissionPlanRes & IBaseResponse>(data);
  };

  return useMutation(addCommissionPlanApi, {
    onSuccess: async (rsp, variables) => {
      if (rsp.success && 'plan' in rsp) {
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.COMMISSIONS.GET_PLANS] });

        const assignData = {
          commissionPlan: {
            id: rsp.plan.id,
            name: rsp.plan.name,
          },
        };

        await assignMembers(assignData as Omit<TAssignCommissionPlansReq, 'shopName' & 'memberEmails'>);

        posthog.capture('ManageMate_web', {
          shopName: personnel?.shopName,
          newMember: personnel?.email,
          addedAt: new Date().toISOString(),
          action: 'user_added_commission_plan',
        });

        navigate(ROUTES.COMMISSION_PLANS);
        enqueueSnackbar(`${t('commissionPlanWasCreated')}`, { variant: 'success' });
      } else if (rsp?.error && rsp.error?.response?.status) {
        handleReqError(rsp.error, variables);
      } else if ('msg' in rsp) {
        console.error(`ERROR! add commission plan failed! ${rsp.msg}`);
      }

      return rsp;
    },
    onError: (error: TAxiosError) => {
      console.error(`ERROR! add commission plan request threw an Exception! ${error}`);
    },
  });
}

export function useDeleteCommissionPlanAPI() {
  const { personnel } = useAppSelector(getAuthenticateUser);
  const selectedCommissionPlan = useAppSelector(getSelectedCommissionPlan);
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();
  const { t } = useTranslation('commissions');
  const queryClient = useQueryClient();

  const { handleReqError } = useError();

  const deleteCommissionPlanApi = () => {
    const data = {
      planId: selectedCommissionPlan.id as string,
      shopName: personnel.shopName,
    };

    return CommissionsController().deleteCommissionPlan<IBaseResponse>(data);
  };

  return useMutation(deleteCommissionPlanApi, {
    onSuccess: async (rsp, variables) => {
      if (rsp.success) {
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.COMMISSIONS.GET_PLANS] });

        dispatch(commissionsActions.setToggleDeleteDialog(false));
        enqueueSnackbar(`${t('commissionPlanWasDeletedSuccessfully')}`, { variant: 'success' });
      } else if (rsp?.error && rsp.error?.response?.status) {
        handleReqError(rsp.error, variables);
      } else if ('msg' in rsp) {
        console.error(`ERROR! delete commission plan failed! ${rsp.msg}`);
      }

      return rsp;
    },
    onError: (error: TAxiosError) => {
      console.error(`ERROR! delete commission plan request threw an Exception! ${error}`);
    },
  });
}

export function useUpdateCommissionsPlanAPI() {
  const { personnel } = useAppSelector(getAuthenticateUser);
  const { mutateAsync: assignMembers } = useAssignCommissionPlanAPI();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation('commissions');

  const navigate = useNavigate();

  const { handleReqError } = useError();

  const queryClient = useQueryClient();

  const tokenDetails = useAppSelector(getTokenDetails);

  const memberEmails = useAppSelector(getMemberEmails);

  const selectedCommissionPlan = useAppSelector(getSelectedCommissionPlan);

  const updateCommissionPlanApi = (extraData: Omit<TAddCommissionPlanReq['planData'], 'creator' & 'updatedBy'>) => {
    const data = {
      planData: {
        ...extraData,
        id: selectedCommissionPlan.id,
        creator: selectedCommissionPlan.creator,
        updatedBy: {
          name: personnel.shopName,
          email: personnel.email,
          avatarFile: personnel.avatarFile || '',
          locale: tokenDetails?.settings?.ui?.locale || 'en',
        },
      },
      shopName: personnel.shopName,
    } as TAddCommissionPlanReq;

    return CommissionsController().editCommissionPlan<TAddCommissionPlanRes & IBaseResponse>(data);
  };

  return useMutation(updateCommissionPlanApi, {
    onSuccess: async (rsp, variables) => {
      if (rsp?.success) {
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.COMMISSIONS.GET_PLANS] });

        const assignData = {
          commissionPlan: {
            id: selectedCommissionPlan.id,
            name: variables.name,
          },
        };

        if (memberEmails?.length > 0) await assignMembers(assignData as Omit<TAssignCommissionPlansReq, 'shopName' & 'memberEmails'>);

        navigate(ROUTES.COMMISSION_PLANS);
        enqueueSnackbar(`${t('commissionPlanWasUpdated')}`, { variant: 'success' });
      } else if (rsp?.error && rsp.error?.response?.status) {
        handleReqError(rsp.error, variables);
      } else if ('msg' in rsp) {
        console.error(`ERROR! update commission plan failed! ${rsp.msg}`);
      }

      return rsp;
    },
    onError: (error: TAxiosError) => {
      console.error(`ERROR! update commission plan request threw an Exception! ${error}`);
    },
  });
}

export function useAssignBulkCommissionPlanAPI() {
  const { personnel } = useAppSelector(getAuthenticateUser);
  const { refetch } = useGetTeamAPI();

  const { handleReqError } = useError();

  const assignBulkCommissionPlanApi = (assignData: Array<{ commissionPlan: TCommissionPlan, memberEmails: string[] }>) => {
    const data = {
      shopName: personnel.shopName,
      assignData,
    };

    return CommissionsController().assignPlanBulk<IBaseResponse>(data);
  };

  return useMutation(assignBulkCommissionPlanApi, {
    onSuccess: async (rsp, variables) => {
      if (rsp.success) {
        await refetch();
      } else if (rsp?.error && rsp.error?.response?.status) {
        handleReqError(rsp.error, variables);
      } else if ('msg' in rsp) {
        console.error(`ERROR! assign bulk commission plan failed! ${rsp.msg}`);
      }

      return rsp;
    },
    onError: (error: TAxiosError) => {
      console.error(`ERROR! assign bulk commission plan request threw an Exception! ${error}`);
    },
  });
}

export function useGetTeamsDataAPI(isMainTeamPage = false) {
  const dispatch = useAppDispatch();
  const { handleReqError } = useError();

  const { personnel } = useAppSelector(getAuthenticateUser);

  return useQuery(
    [`${QUERY_KEYS.COMMISSIONS.GET_TEAMS}-${personnel?.email}`],
    async () => {
      const data = {
        shopName: personnel.shopName,
      };

      const rsp = await CommissionsController().getTeamsData<TGetCommissionTeamsRes>(data);

      if (rsp?.success && 'commissionTeams' in rsp) {
        dispatch(commissionsActions.setCommissionTeams(rsp.commissionTeams));
      } else if (rsp?.error && rsp.error?.response?.status) {
        handleReqError(rsp.error, data);
      } else if ('msg' in rsp) {
        console.error(`ERROR! get teams data failed! ${rsp.msg}`);
      }

      return rsp;
    },
    {
      staleTime: 3 * (60 * 1000),
      suspense: false,
      enabled: isMainTeamPage,
    },
  );
}

export function useAddTeamAPI() {
  const queryClient = useQueryClient();
  const { personnel } = useAppSelector(getAuthenticateUser);
  const dispatch = useAppDispatch();
  const { handleReqError } = useError();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation('team');
  const posthog = usePostHog();

  const addTeamApi = (data: Omit<TAddCommissionTeamReq, 'shopName'>) => {
    const team = {
      ...data,
      shopName: personnel?.shopName,
    };

    return CommissionsController().addCommissionTeam<IBaseResponse>(team);
  };

  return useMutation(addTeamApi, {
    onSuccess: async (rsp, variables) => {
      if (rsp?.success) {
        await queryClient.invalidateQueries({ queryKey: [`${QUERY_KEYS.COMMISSIONS.GET_TEAMS}-${personnel?.email}`] });

        enqueueSnackbar(t('teamWasAdded'), { variant: 'success' });

        posthog.capture('ManageMate_web', {
          shopName: personnel?.shopName,
          newMember: personnel?.email,
          addedAt: new Date().toISOString(),
          action: 'user_added_commission_team',
        });

        dispatch(commissionsActions.setIsOpenTeamCommissionsNav(false));
      } else if (rsp?.error && rsp.error?.response?.status) {
        handleReqError(rsp.error, variables);
      } else if ('msg' in rsp) {
        console.error(`ERROR! add team failed! ${rsp.msg}`);
      }
    },
    onError: (error: TAxiosError) => {
      console.error(`ERROR! add team request threw an Exception! ${error}`);
    },
  });
}

export function useDeleteTeamAPI() {
  const { personnel } = useAppSelector(getAuthenticateUser);
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation('team');
  const { handleReqError } = useError();

  const deleteTeam = (teamId: number) => {
    const data = {
      teamId,
      shopName: personnel.shopName,
    };

    return CommissionsController().deleteCommissionTeam<IBaseResponse>(data);
  };

  return useMutation(deleteTeam, {
    onSuccess: async (rsp, variables) => {
      if (rsp?.success) {
        await queryClient.invalidateQueries({ queryKey: [`${QUERY_KEYS.COMMISSIONS.GET_TEAMS}-${personnel?.email}`] });

        dispatch(commissionsActions.setIsOpenDeleteTeamDialog(false));
        enqueueSnackbar(`${t('teamWasDeleted')}`, { variant: 'success' });
      } else if (rsp?.error && rsp.error?.response?.status) {
        handleReqError(rsp.error, variables);
      } else if ('msg' in rsp) {
        console.error(`ERROR! delete team commission request failed! ${rsp.msg}`);
      }
    },
    onError: (error: TAxiosError) => {
      console.error(`ERROR! delete team commission request threw an Exception! ${error}`);
    },
  });
}

export function useUpdateTeamPlanAPI() {
  const { personnel } = useAppSelector(getAuthenticateUser);
  const { handleReqError } = useError();

  const updateTeamPlan = (payload: Omit<TUpdateCommissionTeamPlanReq, 'shopName'>) => {
    const data = {
      ...payload,
      shopName: personnel.shopName,
    };

    return CommissionsController().updateCommissionTeamPlan<IBaseResponse>(data);
  };

  return useMutation(updateTeamPlan, {
    onSuccess: async (rsp, variables) => {
      if (!rsp?.success) {
        if (rsp?.error && rsp.error?.response?.status) {
          handleReqError(rsp.error, variables);
        } else if (!rsp?.error && 'msg' in rsp) {
          console.error(`ERROR! update team commission plan request failed! ${rsp.msg}`);
        }
      }
    },
    onError: (error: TAxiosError) => {
      console.error(`ERROR! update team commission plan request threw an Exception! ${error}`);
    },
  });
}

export function useUpdateTeamMembersAPI() {
  const { personnel } = useAppSelector(getAuthenticateUser);
  const queryClient = useQueryClient();
  const { handleReqError } = useError();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation('team');
  const dispatch = useAppDispatch();

  const updateTeamMembers = (payload: Omit<TUpdateCommissionTeamMembersReq, 'shopName'>) => {
    const data = {
      ...payload,
      shopName: personnel.shopName,
    };

    return CommissionsController().updateCommissionTeamMembers<IBaseResponse>(data);
  };

  return useMutation(updateTeamMembers, {
    onSuccess: async (rsp, variables) => {
      if (rsp?.success) {
        await queryClient.invalidateQueries({ queryKey: [`${QUERY_KEYS.COMMISSIONS.GET_TEAMS}-${personnel?.email}`] });

        dispatch(commissionsActions.setIsOpenTeamCommissionsNav(false));
        enqueueSnackbar(t('teamWasUpdated'), { variant: 'success' });
      } else if (rsp?.error && rsp.error?.response?.status) {
        handleReqError(rsp.error, variables);
      } else if ('msg' in rsp) {
        console.error(`ERROR! update team commission members request failed! ${rsp.msg}`);
      }
    },
    onError: (error: TAxiosError) => {
      console.error(`ERROR! update team commission members request threw an Exception! ${error}`);
    },
  });
}

export function useGetProductAPI() {
  const { personnel } = useAppSelector(getAuthenticateUser);
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation('commissions');

  const { handleReqError } = useError();

  const productVariantApi = (payload: {
    variantId?: string;
    sku?: string;
    uidType: 'variantId' | 'sku';
  }) => {
    const data = {
      ...payload,
      shopName: personnel.shopName,
    };

    return CommissionsController().getProductVariant<TGetProductVariantRes>(data);
  };

  return useMutation(productVariantApi, {
    onSuccess: async (rsp, variables) => {
      if (rsp.success) {
        if ('productVariant' in rsp && rsp.productVariant) {
          dispatch(
            commissionsActions.setSelectedProduct({
              ...rsp.productVariant,
              fixedType: 0,
            }),
          );
        } else {
          enqueueSnackbar(t('noProductFound'), {
            variant: 'warning',
          });
        }
      } else if (rsp?.error && rsp.error?.response?.status) {
        handleReqError(rsp.error, variables);
      } else if ('msg' in rsp) {
        console.error(`ERROR! get product variant plan failed! ${rsp.msg}`);
      }

      return rsp;
    },
    onError: (error: TAxiosError) => {
      console.error(`ERROR! get product variant request threw an Exception! ${error}`);
    },
  });
}
